import AddIcon from "@mui/icons-material/Add";
import { Button, Divider, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useAndonWorkstationsByFcIds } from "~/hooks/useAndonWorkstationsByFcIds";
import { groupArrayByKey } from "~/lib/helpers";
import { AndonWorkstation } from "~/types/api";

export type FcGridSelectionProps = {
  fcId?: string;
  workingWorkstationIds: string[];
  onAddWorkstations: (workstationIds: string[], fcId?: string) => void;
};

export const workstationIdsSortedByDeviceId = (
  workstations: AndonWorkstation[]
): string[] =>
  [...workstations]
    .sort((a, b) => a.deviceId.localeCompare(b.deviceId))
    .map((workstation) => workstation.workstationId);

export default function FcGridSelection({
  fcId,
  workingWorkstationIds,
  onAddWorkstations
}: FcGridSelectionProps) {
  const { t } = useTranslation();
  const { workstations: fetchedWorkstations, isError } =
    useAndonWorkstationsByFcIds(fcId ? [fcId] : [], { poll: false });
  const workstations = fetchedWorkstations ?? [];

  if (!fetchedWorkstations && !isError) {
    return null;
  }

  if (workstations.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("no workstations")}
      </Typography>
    );
  }

  const workingWorkstationIdSet = new Set(workingWorkstationIds);
  const groupedWorkstations = groupArrayByKey(
    workstations,
    (workstation) => workstation.autostoreGridId
  );
  const workstationsNotOnWorkingLayoutCount = workstations.filter(
    (workstation) => !workingWorkstationIdSet.has(workstation.workstationId)
  ).length;

  return (
    <Stack spacing={1}>
      <Button
        size="small"
        variant="subtle"
        color="primary"
        startIcon={<AddIcon />}
        disabled={workstationsNotOnWorkingLayoutCount === 0}
        onClick={() =>
          onAddWorkstations(workstationIdsSortedByDeviceId(workstations), fcId)
        }
      >
        {workstationsNotOnWorkingLayoutCount === 0
          ? t("added")
          : `${t("add all workstations")} (${workstationsNotOnWorkingLayoutCount})`}
      </Button>
      <Divider />
      {Object.entries(groupedWorkstations).map(
        ([autostoreGridId, gridWorkstations]) => {
          const gridWorkstationCount = gridWorkstations.length;
          const gridWorkstationsNotOnWorkingLayoutCount =
            gridWorkstations.filter(
              (workstation) =>
                !workingWorkstationIdSet.has(workstation.workstationId)
            ).length;
          const label =
            gridWorkstationsNotOnWorkingLayoutCount === 0
              ? t("added")
              : gridWorkstationsNotOnWorkingLayoutCount === gridWorkstationCount
                ? t("add")
                : t("add count", {
                    count: gridWorkstationsNotOnWorkingLayoutCount
                  });
          return (
            <Stack
              key={autostoreGridId}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="body2">
                {gridWorkstations[0].autostoreGridName} ({gridWorkstationCount})
              </Typography>
              <Button
                size="small"
                variant="subtle"
                color="primary"
                disabled={gridWorkstationsNotOnWorkingLayoutCount === 0}
                onClick={() =>
                  onAddWorkstations(
                    workstationIdsSortedByDeviceId(gridWorkstations),
                    fcId
                  )
                }
              >
                {label}
              </Button>
            </Stack>
          );
        }
      )}
    </Stack>
  );
}
